import { useEffect, useRef } from 'react'
import { TextField } from '@mui/material'
import flatpickr from 'flatpickr'
import { French } from 'flatpickr/dist/l10n/fr.js'
import type { Instance } from 'flatpickr/dist/types/instance'
import 'flatpickr/dist/flatpickr.min.css'
import { versIso } from './ChampDate.js'

/**
 * Sélecteur de DATE ET HEURE, sur flatpickr — le même calendrier que
 * `ChampDate`, avec l'horloge en plus.
 *
 * ⚠️ `<input type="datetime-local">` a été essayé d'abord : son apparence est
 * celle du navigateur et change d'un poste à l'autre, sa saisie d'heure est
 * une suite de petits champs qu'il faut franchir à la flèche, et son `step` ne
 * contraint que le sélecteur — une heure tapée au clavier le traverse. Le même
 * raisonnement que pour la date vaut ici, et c'est pourquoi les deux champs
 * partagent maintenant le même calendrier.
 *
 * ⭐ Le PAS est celui de la grille de l'agenda. Le sélecteur n'offre alors que
 * des heures réservables : proposer 10 h 07 sur une grille qui ne fait rien
 * commencer avant 10 h 30 serait proposer une fermeture à moitié vide.
 */

/**
 * `AAAA-MM-JJTHH:MM` à partir des composantes LOCALES.
 *
 * ⚠️ Surtout pas `toISOString()`, qui convertit d'abord en UTC : l'heure
 * choisie reviendrait décalée, et c'est l'appelant — qui seul connaît le fuseau
 * de la BOUTIQUE — qui décide de la conversion en instant.
 */
export function versIsoHeure(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${versIso(d)}T${h}:${m}`
}

export function ChampDateHeure({
  label,
  value,
  onChange,
  pas = 1,
  min,
  error,
  helperText,
  sx,
}: {
  label: string
  /** `AAAA-MM-JJTHH:MM`, ou chaîne vide. */
  value: string
  onChange: (valeur: string) => void
  /** Pas des minutes, en minutes — celui de Paramètres › Agenda. */
  pas?: number
  min?: string | Date
  error?: boolean
  helperText?: string
  sx?: object
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const fpRef = useRef<Instance | null>(null)
  // `onChange` est recréé à chaque rendu du parent : le garder dans une
  // référence évite de détruire et reconstruire le calendrier à chaque frappe
  // ailleurs dans le formulaire.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (!inputRef.current) return

    const instance = flatpickr(inputRef.current, {
      locale: French,
      enableTime: true,
      // ⚠️ 24 h, sans AM/PM : c'est l'écriture des horaires d'ouverture et
      // celle de la grille. Deux notations sur le même écran se lisent mal.
      time_24hr: true,
      dateFormat: 'd/m/Y H:i',
      minuteIncrement: pas,
      ...(min !== undefined ? { minDate: min } : {}),
      // La saisie au clavier reste possible : au comptoir, taper une date va
      // plus vite que de la chercher au calendrier.
      allowInput: true,
      onChange: (dates) => {
        onChangeRef.current(dates[0] ? versIsoHeure(dates[0]) : '')
      },
    })
    fpRef.current = instance

    return () => {
      instance.destroy()
      fpRef.current = null
    }
    // ⚠️ Dépendances VIDES à dessein, comme pour `ChampDate` : les inclure
    // ferait détruire puis reconstruire le calendrier à chaque rendu du parent.
  }, [])

  /**
   * ⚠️ La BORNE BASSE peut changer en cours de vie du champ — « Au » ne
   * descend pas sous « Du », qui se saisit juste avant. Le calendrier est
   * construit une fois pour toutes : sans cette mise à jour, il garderait la
   * borne du premier rendu, c'est-à-dire aucune.
   *
   * La dépendance est une CHAÎNE : `new Date(...)` rend un objet neuf à chaque
   * rendu du parent, et l'effet se rejouerait sans fin.
   */
  const minCle = min === undefined ? '' : min instanceof Date ? min.toISOString() : min
  const minRef = useRef(min)
  minRef.current = min
  useEffect(() => {
    fpRef.current?.set('minDate', minRef.current ?? null)
  }, [minCle])

  // Valeur poussée de l'extérieur — ouverture du formulaire, remise à zéro.
  // `false` : ne pas rejouer `onChange`, ce qui bouclerait.
  useEffect(() => {
    const fp = fpRef.current
    if (!fp) return
    const courante = fp.selectedDates[0] ? versIsoHeure(fp.selectedDates[0]) : ''
    if (courante === value) return
    /**
     * ⚠️ Un objet `Date`, jamais la chaîne : `setDate` l'interpréterait selon
     * `dateFormat` — « d/m/Y H:i » — où « 2026-10-21T10:30 » est illisible, et
     * le champ resterait vide alors que la valeur est bien fournie.
     *
     * Sans suffixe `Z`, le navigateur lit cette chaîne à l'heure LOCALE, ce qui
     * est exactement ce qu'on veut : le champ affiche ce que l'on a saisi.
     */
    fp.setDate(value ? new Date(value) : '', false)
  }, [value])

  return (
    <TextField
      label={label}
      placeholder="JJ/MM/AAAA HH:MM"
      inputRef={inputRef}
      error={error}
      helperText={helperText}
      InputLabelProps={{ shrink: true }}
      sx={sx}
    />
  )
}
